import * as format from 'date-fns/format'
import { normalize, schema } from 'normalizr'
import { parsePhoneNumber, getCountryCallingCode } from 'react-phone-number-input'
import { topUpFee } from 'src/constants/wallet'
import { EN_ROUTE, REQUESTING, SCHEDULED_TRIPS, WAITING_FOR_PICKUP } from '../state/constants/index'
import { IUserPhone, IUserPhonesAsObject } from '../types/user'
import UIMessages from './ui-messages'
import history from 'src/history'

export function formatPhone(phoneNumber: string, countryCode: string): string {
  let phoneStr = phoneNumber
  if (phoneStr.length !== 10) {
    return phoneStr
  }

  phoneStr = `(${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`

  if (typeof countryCode === 'number') {
    phoneStr = `+${countryCode} ${phoneStr}`
  }

  return phoneStr
}

export function phoneNumberSplit(phoneNumber: string): any {
  if (!phoneNumber || phoneNumber === '') {
    return {
      countryCode: '',
      phoneNumber: ''
    }
  }
  const countryCode = getCountryCallingCode(parsePhoneNumber(phoneNumber).country)
  return {
    countryCode,
    phoneNumber: phoneNumber.slice(countryCode.length + 1)
  }
}

export function formatTime(time: number): string {
  return format(time, 'dddd, MMMM, D, H:m')
}

export const convertCentsToDollars = (centsValue: number) => {
  return centsValue / 100
}

export const convertDollarsToCents = (centsValue: number) => {
  return centsValue * 100
}

export function capitalToUpperCase(val: string): string {
  return val.charAt(0).toUpperCase() + val.slice(1)
}

//don't change the order. list of active trips is based on this
export const tripStages = [
  {
    label: UIMessages.trip.statuses.requesting,
    route: 'requesting',
    value: REQUESTING
  },
  {
    label: UIMessages.trip.statuses.waitingForPickUp,
    route: 'waiting-for-pick-up',
    value: WAITING_FOR_PICKUP
  },
  {
    label: UIMessages.trip.statuses.enRoute,
    route: 'en-route',
    value: EN_ROUTE
  },
  {
    label: UIMessages.trip.statuses.scheduled,
    route: 'scheduled-trips',
    value: SCHEDULED_TRIPS
  }
]

export const statuses = {
  REQUESTING: 'Requesting',
  WAITING_FOR_PICKUP: 'ETA',
  EN_ROUTE: 'ETD',
  SCHEDULED_TRIPS: 'Scheduled',
  REQUESTED: 'Requested',
  ACCEPTED: 'Accepted',
  DRIVING_TO_PICK_UP: 'Driving to pick up',
  ARRIVED_AT_PICK_UP: 'Arrived at pick up',
  PICKED_UP: 'Picked up',
  DRIVING_TO_DROP_OFF: 'Driving to drop off',
  ARRIVED_AT_DROP_OFF: 'Arrived at drop off',
  COMPLETED: 'Completed',
  CANCELLED: 'Canceled',
  REJECTED: 'Rejected'
}

export function convertArrayToObject(data: any, name: string) {
  const convertSchema = new schema.Entity(name, undefined, {
    idAttribute: (value: { id: any }) => value.id
  })
  return normalize(data, [convertSchema])
}

export function centsToDollars(cents: number): string {
  return (Math.floor(cents) / 100).toString()
}

export function secondsToHours(seconds: number, displaySeconds?: boolean) {
  // Calculate hours by dividing seconds by 3600 (seconds in an hour), then pad to ensure two digits
  const hh = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, '0')

  // Calculate minutes by first finding the remainder of seconds after extracting hours,
  // then dividing by 60 (seconds in a minute), and pad to ensure two digits
  const mm = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0')

  if (displaySeconds === true) {
    const ss = (seconds % 60).toString().padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }
  return `${hh}:${mm}`
}

export function minutesToHours(minutes: number, displaySeconds?: boolean) {
  const hh = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')
  const mm = (Math.round(minutes) % 60).toString().padStart(2, '0')

  return `${hh}:${mm}`
}

export function secondsToMinutes(seconds: number, displaySeconds?: boolean) {
  if (displaySeconds === true) {
    return `${Math.round(seconds / 60)} mins ${seconds % 60} secs`
  }
  return `${Math.round(seconds / 60)} mins`
}

export function metersToMiles(meters: number) {
  return (meters / 1609.344).toFixed(2)
}

export function milesToMeters(miles: number) {
  return (miles * 1609.344).toFixed(2)
}

export const getCurrentUnixTime = () => {
  return Math.round(new Date().getTime() / 1000.0)
}

export const convertUserPhonesArrayToObject = (phones: IUserPhone[]): IUserPhonesAsObject => {
  return phones.reduce((acc, cur) => ({ ...acc, [cur.type]: cur }), {} as IUserPhonesAsObject)
}

export const addTime = (startTime: any, endTime: any) => {
  if (startTime && endTime) {
    const startArr = startTime.split(':')
    const endArr = endTime.split(':')
    for (let i = 0; i < startArr.length; i++) {
      startArr[i] = startArr[i] ? parseInt(startArr[i], 10) : 0
      endArr[i] = endArr[i] ? parseInt(endArr[i], 10) : 0
    }
    const hour = startArr[0] + endArr[0]
    const minute = startArr[1] + endArr[1]
    const second = startArr[2] + endArr[2]
    const min = timeConvert(minute)
    const sec = timeConvert(second)
    const hours = String(hour + min.rx).padStart(2, '0')
    const minutes = String(min.ry + sec.rx).padStart(2, '0')
    const seconds = String(sec.ry).padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
  }
  return startTime
}
//converter for hour to hh:mm or minute to mm:ss
const timeConvert = (n: number) => {
  const num = n
  const x = num / 60
  const rx = Math.floor(x)
  const y = (x - rx) * 60
  const ry = Math.round(y)
  // rx being the bigger time unit
  return { rx, ry }
}

export const topUpDetails = (amount: number, transactionFee: number) => {
  const transactionAmount = amount / 100

  if (isNaN(transactionFee) || transactionFee === 0) {
    return {
      netAmount: transactionAmount,
      totalFee: 0
    }
  }

  const totalFee = (transactionFee / 100) * transactionAmount
  const netAmount = transactionAmount - totalFee

  return {
    netAmount,
    totalFee
  }
}

export const handleRedirect = (path: string) => {
  history.push(path)
}

export const parseCamelCase = (text: string) => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces between camelCase words
    .replace(/(\w)([A-Z]{2,})$/g, '$1 $2') // Handle the case where the last two letters are uppercase
    .replace(/\s+/g, ' ') // Remove extra spaces if any
    .trim() // Trim leading/trailing spaces
    .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize the first letter of each word
}

export const percentToDecimal = (value: string | number): number => {
  const num = typeof value === 'string' ? Number(value) : value
  return num / 100
}

export const decimalToPercent = (value: string | number): number => {
  const num = typeof value === 'string' ? Number(value) : value
  return num * 100
}

const ABBREVIATED_TEXT = {
  AH: 'After Hours',
  HD: 'Holiday',
  WE: 'Weekend'
}

export const parseDiscountLabel = (label: string) => {
  if (!label.trim()) {
    return ''
  }

  const titleCase = parseCamelCase(label)
  const labelName = titleCase
    .split(' ')
    .map(text => {
      return ABBREVIATED_TEXT[text as keyof typeof ABBREVIATED_TEXT]
        ? ABBREVIATED_TEXT[text as keyof typeof ABBREVIATED_TEXT]
        : text
    })
    .join(' ')
  return labelName
}



// WEBPACK FOOTER //
// ./src/utils/index.ts